import { computed, observable } from 'mobx';
import { OperationLogger } from '@tencent/slide-sdk-devtools';
import { IPresentationDataModel, ISlideFileData } from '@tencent/slide-sdk-model';
import { ISectionManager } from '@src/slide/application/service/section-manager/section-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { ISlideRenderStateManager } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import PresentationModel from '@src/slide/domain/ui-model/element-modeles/presentation-model';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { SlideDataServices } from '../../slide-app/core/slide-data-services.core';
import { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

export class UiDataService implements IUiDataService {
    @observable public presentation: IPresentationModel;

    public asyncConvertPresentation = true;
    public needConvertChildren = true;
    public operationLogger?: OperationLogger;
    private selectionDelegate?: ISelectionService;
    private slideRenderStateManager?: ISlideRenderStateManager;
    private sectionManagerDelegate?: ISectionManager;
    private sdkPresentation: IPresentationDataModel;

    public init(): void {
        this.presentation = new PresentationModel({
            selectionService: this.getSelectionDelegate(),
        });
        if (this.sectionManagerDelegate) {
            this.presentation.setSectionManagerDelegate(this.sectionManagerDelegate);
        }

        this.initDataVersion3().catch((err) => {
            console.error('[UiDataService] failed to init SlideApp(v3):', err.message);
        });
    }

    public async initWithData(slideFileData: ISlideFileData): Promise<void>  {
        this.presentation = new PresentationModel({
            selectionService: this.getSelectionDelegate(),
        });
        if (this.sectionManagerDelegate) {
            this.presentation.setSectionManagerDelegate(this.sectionManagerDelegate);
        }

        await SlideDataServices.SlideAppService.promise();
        let presentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService();
        if (slideFileData) {
            presentationStoreService = presentationStoreService?.createFromSlideFileData(slideFileData);
        }
        this.reInitDataVersion3(presentationStoreService?.getPresentation());
        eventBus.emit(EventKeys.UI_MODEL_BIND_SDK, { slideCount: this.getSlideCount(), slides: this.slideModelList });
    }

    public setSelectionDelegate(selection: ISelectionService): void {
        if (this.selectionDelegate) return;
        this.selectionDelegate = selection;
        if (this.presentation) {
            this.presentation.selectionService = selection;
        }
    }

    public setSectionManagerDelegate(sectionManager: ISectionManager): void {
        if (this.sectionManagerDelegate) return;
        this.sectionManagerDelegate = sectionManager;
        if (this.presentation) {
            this.presentation.setSectionManagerDelegate(sectionManager);
        }
    }

    public setSlideRenderStateManagerDelegate(slideRenderStateManager: ISlideRenderStateManager): void {
        if (this.slideRenderStateManager) return;
        this.slideRenderStateManager = slideRenderStateManager;
    }

    // /**
    //  * 导入PPT
    //  * @param json
    //  */
    // public loadNewPresentation(json): void {
    //     SDKStore.loadNewPresentation(json);
    // }

    @computed public get slideModelList(): ISlideModel[] {
        return this.presentation?.children?.slice(0, this.presentation.children.length);
    }

    @computed public get masterModelList(): IMasterModel[] {
        return this.presentation?.masterList?.slice(0, this.presentation.masterList.length);
    }

    @computed public get layoutModelList(): ILayoutModel[] {
        // 拍平 masterList 中的 layoutList 获得一维的 layoutList
        const layoutList = this.presentation?.masterList?.reduce((pre: ILayoutModel[], cur) => {
            const masterLayoutList: ILayoutModel[] = cur.layoutList.slice(0, cur.layoutList.length);
            return pre.concat(masterLayoutList);
        }, []);

        return layoutList;
    }

    public async forceConvertSlides(slides: ISlideModel[] = this.presentation?.children || []): Promise<any> {
        return Promise.all(slides
            .map(async (slide) => {
                !slide.childrenConverted && await slide.startConvertChildrenTask(TaskPriority.HIGH);
            }));
    }

    /**
     * 手动绑定
     * @param slideAppV3
     */
    public async reInitDataVersion3(presentationData?: IPresentationDataModel) {
        if (!presentationData) {
            throw new Error('[UiDataService] unexpected presentationStoreService is undefined');
        }
        this.sdkPresentation = presentationData;
        this.presentation.operation.__firstLoad = true;
        this.presentation.asyncConvertChildren = this.asyncConvertPresentation;
        this.presentation.asyncConvertSlideChildren = this.asyncConvertPresentation;
        this.presentation.needConvertChildren = this.needConvertChildren;
        eventBus.emit(EventKeys.PRESENTATION_CONVERT_START);
        await this.presentation.bindSDK(this.sdkPresentation);
        this.presentation.converted = true;
        eventBus.emit(EventKeys.PRESENTATION_CONVERTED);
    }

    private getSlideCount(): number {
        return SlideDataServices.States.SlideCountState()?.slideCount ?? 0;
    }

    private getSelectionDelegate(): ISelectionService | undefined {
        return this.selectionDelegate;
    }

    // ================================================分割线，以下为数据3.0相关逻辑========================================//


    /**
     * 数据3.0相关逻辑
     */
    private async initDataVersion3() {
        await SlideDataServices.SlideAppService.promise();
        const presentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService();
        this.reInitDataVersion3(presentationStoreService?.getPresentation());
        eventBus.emit(EventKeys.UI_MODEL_BIND_SDK, { slideCount: this.getSlideCount(), slides: this.slideModelList });
    }
}
